// "Show what the host sees": the conversation as the host stores it, metadata and the
// ciphertext's size per event, with no content. Drawn from the same relayed frames the chat
// decrypts, so it is exactly what this browser received.
import { LockKey } from "@phosphor-icons/react";

import { useT } from "../i18n";
import { clockTime } from "../workloads/format";
import { hostRow, type HostRow } from "./hostRows";
import type { ConversationState } from "./model";

const CIPHER = {
  backgroundImage: "repeating-linear-gradient(90deg, rgb(var(--gray-300) / 0.9) 0 6px, transparent 6px 9px)",
};

function Row({ row }: { row: HostRow }) {
  const t = useT().conversation.host;
  const width = Math.min(100, 18 + row.bytes / 5);
  return (
    <div
      className="grid grid-cols-[34px_1fr_56px] items-center gap-3 rounded-lg border border-dashed border-muted px-3 py-2.5 font-mono text-[11.5px] text-gray-500 sm:grid-cols-[34px_70px_88px_1fr_64px]"
      data-testid="host-row"
    >
      <span>#{row.seq}</span>
      <span className="hidden sm:inline">{row.at === undefined ? "—" : clockTime(row.at)}</span>
      <span className="truncate text-gray-800">{row.type}</span>
      <span className="hidden min-w-0 items-center gap-2 sm:flex">
        {row.tool ? <span className="truncate text-gray-700">{row.tool}</span> : null}
        <span className="h-2.5 min-w-[40px] rounded-sm" style={{ ...CIPHER, width: `${width}%` }} />
      </span>
      <span className="text-end">{t.bytes(row.bytes)}</span>
    </div>
  );
}

export function HostRecord({ state }: { state: ConversationState }) {
  const t = useT().conversation.host;
  const rows = state.events.map(hostRow);
  return (
    <div className="space-y-2" data-testid="host-record">
      <div className="mb-3 flex items-start gap-2.5 rounded-lg bg-gray-100 p-3 text-[12.5px] text-gray-600">
        <LockKey weight="fill" className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
        <p className="m-0">{t.intro(<span className="font-mono">ocs-capture</span>)}</p>
      </div>
      {rows.length === 0 ? <p className="m-0 text-[13px] text-gray-500">{t.noEvents}</p> : null}
      {rows.map((r) => (
        <Row key={r.key} row={r} />
      ))}
    </div>
  );
}
